import { Layout } from "@common/ADAPT.Common.Model/organisation/layout";
import { IBreezeEntity } from "@common/lib/data/breeze-entity.interface";
import { ReplaySubject } from "rxjs";

/** Options for column which has not been loaded yet */
export interface ILayoutBaseColumnOptions {
    enabled: boolean;
    manuallyEnabled?: boolean;
    components: ILayoutBaseComponent[];

    [option: string]: any;
}

/** Component that has not been loaded yet */
export interface ILayoutBaseComponent {
    id: string;
    size?: string;
    element?: HTMLElement;
    ref?: ILayoutUpdateListener<ILayoutBaseComponent>;
    options?: ILayoutComponentOptions;
}

/** Column that has not been loaded yet */
export interface ILayoutBaseColumn {
    size?: string;
    element?: HTMLElement;
    ref?: ILayoutUpdateListener<ILayoutBaseColumn>;
    options: ILayoutBaseColumnOptions;
}

/** Interface a column or component can implement to react to layout updates (normally sizing) */
export interface ILayoutUpdateListener<T> {
    onUpdate(data: T): void;
    updated$: ReplaySubject<void>;
}

/** Options for a layout component */
export interface ILayoutComponentOptions {
    enabled?: boolean;
    userHidden?: boolean;
    displayName?: string;

    [option: string]: any;
}

/** Options for a layout column */
export interface ILayoutColumnOptions {
    enabled: boolean;
    components: ILayoutComponent[];

    [option: string]: any;
}

/** A layout component */
export interface ILayoutComponent extends ILayoutBaseComponent {
    element: HTMLElement;
    ref: ILayoutUpdateListener<ILayoutComponent>;
}

/** A layout column */
export interface ILayoutColumn extends ILayoutBaseColumn {
    ref: ILayoutUpdateListener<ILayoutColumn>;
    element: HTMLElement;
    options: ILayoutColumnOptions;
}

/** Gets the component type given the layout type */
export type TComponentFromLayout<TLayout extends ILayoutBaseColumn> = TLayout["options"]["components"][number];

export interface ILayoutBase<TColumn = ILayoutBaseColumn> {
    columns: TColumn[];
    meta: {
        version: string;
    };
}

export type ILayout = ILayoutBase<ILayoutColumn>;
export interface IEntityWithLayout extends IBreezeEntity {
    layout: Layout | null;
    layoutId: number | null;
    organisationId: number;
}

export enum ColumnSize {
    FullWidth = "Full width (100%)",
    NarrowLeftHand = "Narrow left-hand column (25 / 75%)",
    NarrowLeftHandThird = "Narrow left-hand column (33 / 67%)",
    NarrowRightHand = "Narrow right-hand column (75 / 25%)",
    NarrowRightHandThird = "Narrow right-hand column (67 / 33%)",
    Equal = "Equal columns (50 / 50%)",
}

export const ColumnSizePresetGroups: Record<string, ColumnSize[]> = {
    // ungrouped
    [""]: [ColumnSize.FullWidth, ColumnSize.Equal],
    Thirds: [ColumnSize.NarrowLeftHandThird, ColumnSize.NarrowRightHandThird],
    Quarters: [ColumnSize.NarrowLeftHand, ColumnSize.NarrowRightHand],
};

export const ColumnSizePresets: Record<ColumnSize, string[]> = {
    [ColumnSize.FullWidth]: ["col-md-12"],
    [ColumnSize.NarrowLeftHand]: ["col-md-3", "col-md-9"],
    [ColumnSize.NarrowLeftHandThird]: ["col-md-4", "col-md-8"],
    [ColumnSize.NarrowRightHand]: ["col-md-9", "col-md-3"],
    [ColumnSize.NarrowRightHandThird]: ["col-md-8", "col-md-4"],
    [ColumnSize.Equal]: ["col-md-6", "col-md-6"],
};

export enum ComponentSize {
    Full = "Full Width (100%)",
    ThreeQuarter = "Three Quarter Width (75%)",
    Half = "Half Width (50%)",
    Quarter = "Quarter Width (25%)",
    Third = "Third Width (33%)",
    TwoThird = "Two Third Width (67%)",
}

export const ComponentSizePresetGroups: Record<string, ComponentSize[]> = {
    // ungrouped
    [""]: [ComponentSize.Full, ComponentSize.Half],
    Thirds: [ComponentSize.Third, ComponentSize.TwoThird],
    Quarters: [ComponentSize.Quarter, ComponentSize.ThreeQuarter],
};

export const ComponentSizeWeights: Record<ComponentSize, number> = {
    [ComponentSize.Full]: 1,
    [ComponentSize.ThreeQuarter]: 3/4,
    [ComponentSize.Half]: 1/2,
    [ComponentSize.Quarter]: 1/4,
    [ComponentSize.Third]: 1/3,
    [ComponentSize.TwoThird]: 2/3,
};

export const ComponentSizePresets: Record<ComponentSize, string[]> = {
    // [class name to use, ...matching values]
    // will match this layout preset by either the class name, or any provided matching values
    // an empty string ("") is the default size.
    [ComponentSize.Full]: ["col-md-12", ""],
    [ComponentSize.ThreeQuarter]: ["col-md-9"],
    [ComponentSize.Half]: ["col-md-6"],
    [ComponentSize.Quarter]: ["col-md-3"],
    [ComponentSize.Third]: ["col-md-4"],
    [ComponentSize.TwoThird]: ["col-md-8"],
};
